import React, { useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import {
  Badge,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';

import {
  getComponentListFiltersAreModified,
  getEffectiveComponentListFilters,
  getLevelFilterIsApplicable,
} from '../../../../store/selectors/componentListFilterSelectors';
import {
  ANALYSIS_STATUS_FILTER_VALUES,
  CHECK_STATUS_FILTER_VALUES,
  ComponentListFilters,
  LEVEL_FILTER_VALUES,
  LINKAGE_FILTER_VALUES,
  resetComponentListFilters,
  setComponentListFilters,
} from '../../../../store/slices/applicationSlice';
import { useAppDispatch, useAppSelector } from '../../../hooks';

const FILTER_VALUE_LABELS: { [K in keyof ComponentListFilters]: Record<ComponentListFilters[K], string> } = {
  analysisStatus: { off: 'All', analyzed: 'Analyzed', unanalyzed: 'Unanalyzed' },
  checkStatus: { off: 'All', passing: 'Passing', failing: 'Over-Capacity' },
  level: { off: 'All', visibleLevels: 'Visible Levels', hiddenLevels: 'Hidden Levels' },
  linkage: { off: 'All', linked: 'Linked', unlinked: 'Unlinked' },
};

export function ComponentListFilterControl() {
  const dispatch = useAppDispatch();

  const filters = useAppSelector(getEffectiveComponentListFilters);
  const filtersModifiedFromDefault = useAppSelector(getComponentListFiltersAreModified);
  const levelFilterApplicable = useAppSelector(getLevelFilterIsApplicable);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  function sectionItems<K extends keyof ComponentListFilters>(
    axis: K,
    sectionLabel: string,
    values: readonly ComponentListFilters[K][],
    disabled = false,
  ) {
    return [
      <ListSubheader key={`${axis}-header`} disableSticky>
        {sectionLabel}
      </ListSubheader>,

      ...values.map((value) => (
        <MenuItem
          key={`${axis}-${value}`}
          selected={filters[axis] === value}
          disabled={disabled}
          onClick={() => dispatch(setComponentListFilters({ [axis]: value } as Partial<ComponentListFilters>))}
        >
          <ListItemIcon>{filters[axis] === value && <CheckIcon fontSize="small" />}</ListItemIcon>
          <ListItemText>{FILTER_VALUE_LABELS[axis][value]}</ListItemText>
        </MenuItem>
      )),

      <Divider key={`${axis}-divider`} />,
    ];
  }

  return (
    <>
      <Tooltip title="Filter components">
        <Badge variant="dot" color="info" invisible={!filtersModifiedFromDefault}>
          <IconButton onClick={(event) => setAnchorEl(event.currentTarget)}>
            <FilterAltIcon />
          </IconButton>
        </Badge>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={anchorEl !== null}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClose={() => setAnchorEl(null)}
      >
        {sectionItems('analysisStatus', 'Analysis', ANALYSIS_STATUS_FILTER_VALUES)}
        {sectionItems('checkStatus', 'Checks', CHECK_STATUS_FILTER_VALUES)}
        {sectionItems('level', 'Level', LEVEL_FILTER_VALUES, !levelFilterApplicable)}
        {sectionItems('linkage', 'Layout', LINKAGE_FILTER_VALUES)}

        <MenuItem disabled={!filtersModifiedFromDefault} onClick={() => dispatch(resetComponentListFilters())}>
          <ListItemIcon />
          <ListItemText>Reset filters</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}
